import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bus } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { driverSelfApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate } from '@/utils/format';
import type { TransportAttendanceStatus, TransportTrip, TripRider } from '@/types/transport';
import {
  AttendanceStatusBadge,
  Badge,
  Stat,
  TripStatusBadge,
  delayText,
  formatTime,
  humanise,
  today,
} from './shared';

/** The same three thumb-size marks the operations board uses. */
const MARKS: { value: TransportAttendanceStatus; label: string; tone: string }[] = [
  { value: 'BOARDED', label: 'Boarded', tone: 'bg-success/10 text-success border-success/30' },
  { value: 'ABSENT', label: 'Absent', tone: 'bg-warning/10 text-warning border-warning/30' },
  { value: 'DROPPED_OFF', label: 'Dropped off', tone: 'bg-info/10 text-info border-info/30' },
];

type LifecycleAction = 'start' | 'depart' | 'complete' | 'delay';

/**
 * The road, as a driver sees it.
 *
 * <p>Everything here goes through /transport/me, resolved from the signed-in
 * account on the server — this screen sends no driver identifier and could not
 * read another driver's trips by changing a parameter. It deliberately uses
 * driverSelfApi and the trips.mine/myRoster cache keys, never the staff trips
 * API: a driver holds TRANSPORT_SELF_*, not the office permissions.
 *
 * <p>Built for a phone on a dashboard mount: today's trips as cards, one tap
 * to the manifest, and one tap per child to mark them — a single tap posts a
 * list of one and the fresh roster comes back in the same response.
 */
export function MyTransportPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const records = can(PERMISSIONS.TRANSPORT_SELF_RECORD);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [delaying, setDelaying] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState('');
  const [delayReason, setDelayReason] = useState('');
  const [markingId, setMarkingId] = useState<string | null>(null);

  /* The server's default window: today plus the coming week. */
  const trips = useQuery({
    queryKey: queryKeys.trips.mine({}),
    queryFn: () => driverSelfApi.myTrips(),
  });

  const selected = trips.data?.find((trip) => trip.id === selectedId);

  const roster = useQuery({
    queryKey: queryKeys.trips.myRoster(selectedId ?? ''),
    queryFn: () => driverSelfApi.roster(selectedId ?? ''),
    enabled: selectedId !== null,
  });

  const lifecycle = useMutation({
    mutationFn: (input: { id: string; action: LifecycleAction; minutes?: number; reason?: string }) => {
      if (input.action === 'start') {
        return driverSelfApi.start(input.id);
      }
      if (input.action === 'depart') {
        return driverSelfApi.depart(input.id);
      }
      if (input.action === 'complete') {
        return driverSelfApi.complete(input.id);
      }
      return driverSelfApi.delay(input.id, input.minutes ?? 0, input.reason);
    },
    onSuccess: (trip) => {
      toast.success(`${trip.tripNumber} is now ${humanise(trip.status)}`);
      setDelaying(false);
      setDelayMinutes('');
      setDelayReason('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.trips.all });
    },
    onError: (error) => handleApiError(error),
  });

  const mark = useMutation({
    mutationFn: (input: { tripId: string; enrollmentId: string; status: TransportAttendanceStatus }) =>
      driverSelfApi.mark(input.tripId, {
        marks: [{ enrollmentId: input.enrollmentId, status: input.status }],
      }),
    onMutate: (input) => setMarkingId(input.enrollmentId),
    onSuccess: (fresh, input) => {
      queryClient.setQueryData(queryKeys.trips.myRoster(input.tripId), fresh);
    },
    onError: (error) => handleApiError(error),
    onSettled: () => setMarkingId(null),
  });

  const acting = (action: LifecycleAction) =>
    lifecycle.isPending && lifecycle.variables?.action === action;

  /** The buttons a trip's current state offers. The server re-checks every one. */
  const actionsFor = (trip: TransportTrip) => {
    if (!records) {
      return null;
    }
    const live =
      trip.status === 'SCHEDULED' ||
      trip.status === 'BOARDING' ||
      trip.status === 'IN_PROGRESS' ||
      trip.status === 'DELAYED';
    return (
      <div className="mt-3 flex flex-wrap gap-2">
        {trip.status === 'SCHEDULED' && (
          <Button
            size="sm"
            loading={acting('start')}
            onClick={() => lifecycle.mutate({ id: trip.id, action: 'start' })}
          >
            Open boarding
          </Button>
        )}
        {(trip.status === 'BOARDING' || trip.status === 'DELAYED') && (
          <Button
            size="sm"
            loading={acting('depart')}
            onClick={() => lifecycle.mutate({ id: trip.id, action: 'depart' })}
          >
            Depart
          </Button>
        )}
        {(trip.status === 'IN_PROGRESS' || trip.status === 'DELAYED') && (
          <Button
            size="sm"
            loading={acting('complete')}
            onClick={() => lifecycle.mutate({ id: trip.id, action: 'complete' })}
          >
            Complete
          </Button>
        )}
        {live && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setSelectedId(trip.id);
              setDelaying(true);
            }}
          >
            Report a delay…
          </Button>
        )}
      </div>
    );
  };

  // ------------------------------------------------------------ roster view

  if (selected !== undefined) {
    return (
      <div>
        <PageHeader
          title={selected.routeName}
          description={`${selected.tripNumber} · ${humanise(selected.direction)} · ${formatDate(selected.tripDate)}`}
          icon={<Bus className="h-5 w-5" aria-hidden="true" />}
          action={
            <Button variant="secondary" onClick={() => setSelectedId(null)}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              My trips
            </Button>
          }
        />

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <TripStatusBadge status={selected.status} />
            {selected.status === 'DELAYED' && (
              <span className="text-xs text-[color:var(--sms-warning)]">
                {delayText(selected.delayMinutes)}
                {selected.delayReason !== undefined ? ` — ${selected.delayReason}` : ''}
              </span>
            )}
            <span className="text-sm text-muted">
              planned {formatTime(selected.plannedStartTime)} ·{' '}
              {selected.vehicleName ?? 'no vehicle'}
            </span>
          </div>
          {actionsFor(selected)}
        </Card>

        {roster.isPending ? (
          <LoadingState label="Loading your manifest" />
        ) : roster.error !== null ? (
          <ErrorState error={roster.error} onRetry={() => void roster.refetch()} />
        ) : roster.data === undefined ? null : (
          <>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Stat label="Expected" value={roster.data.expected} />
              <Stat
                label="Boarded"
                value={roster.data.boarded}
                tone={roster.data.boarded > 0 ? 'success' : 'neutral'}
              />
              <Stat
                label="Unmarked"
                value={roster.data.unmarked}
                tone={roster.data.unmarked > 0 ? 'warning' : 'neutral'}
              />
            </div>

            <Card className="mt-4 p-4">
              {roster.data.riders.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">
                  Nobody is enrolled to ride this trip.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {roster.data.riders.map((rider) => (
                    <MyRiderRow
                      key={rider.enrollmentId}
                      rider={rider}
                      canMark={records}
                      busy={mark.isPending && markingId === rider.enrollmentId}
                      onMark={(status) =>
                        mark.mutate({
                          tripId: selected.id,
                          enrollmentId: rider.enrollmentId,
                          status,
                        })
                      }
                    />
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}

        <Modal
          open={delaying}
          onClose={() => setDelaying(false)}
          title="Report a delay"
          description="When the school has delay notices on, every rider's guardian is told."
          footer={
            <>
              <Button variant="secondary" onClick={() => setDelaying(false)}>
                Cancel
              </Button>
              <Button
                loading={acting('delay')}
                disabled={!(Number(delayMinutes) > 0)}
                onClick={() =>
                  lifecycle.mutate({
                    id: selected.id,
                    action: 'delay',
                    minutes: Number(delayMinutes),
                    reason: delayReason.trim() === '' ? undefined : delayReason.trim(),
                  })
                }
              >
                Report it
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <TextField
              label="Minutes late"
              type="number"
              min={1}
              required
              value={delayMinutes}
              onChange={(event) => setDelayMinutes(event.target.value)}
            />
            <TextField
              label="Reason"
              value={delayReason}
              onChange={(event) => setDelayReason(event.target.value)}
              hint="Guardians read this."
            />
          </div>
        </Modal>
      </div>
    );
  }

  // -------------------------------------------------------------- trip list

  const allTrips = trips.data ?? [];
  const todays = allTrips.filter((trip) => trip.tripDate === today());
  const upcoming = allTrips.filter((trip) => trip.tripDate !== today());

  return (
    <div>
      <PageHeader
        title="My transport"
        description="Your trips for today and the coming week. Tap a trip to open its manifest."
        icon={<Bus className="h-5 w-5" aria-hidden="true" />}
      />

      {trips.isPending ? (
        <LoadingState label="Loading your trips" />
      ) : trips.error !== null ? (
        <ErrorState error={trips.error} onRetry={() => void trips.refetch()} />
      ) : allTrips.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted">
          No trips are assigned to you. The transport office puts drivers on routes.
        </Card>
      ) : (
        <div className="space-y-4">
          <section>
            <h2 className="mb-2 text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
              Today
            </h2>
            {todays.length === 0 ? (
              <Card className="p-4 text-sm text-muted">Nothing to drive today.</Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {todays.map((trip) => (
                  <Card key={trip.id} className="p-4">
                    <button
                      type="button"
                      onClick={() => setSelectedId(trip.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-ink">
                            {trip.routeName}
                          </p>
                          <p className="font-mono text-[11px] text-subtle">{trip.tripNumber}</p>
                        </div>
                        <TripStatusBadge status={trip.status} />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
                        <Badge tone={trip.direction === 'PICKUP' ? 'info' : 'neutral'}>
                          {humanise(trip.direction)}
                        </Badge>
                        <span className="tabular-nums">{formatTime(trip.plannedStartTime)}</span>
                        <span className="min-w-0 truncate">{trip.vehicleName ?? 'no vehicle'}</span>
                      </div>
                      {trip.status === 'DELAYED' && (
                        <p className="mt-2 text-xs text-[color:var(--sms-warning)]">
                          {delayText(trip.delayMinutes)}
                          {trip.delayReason !== undefined ? ` — ${trip.delayReason}` : ''}
                        </p>
                      )}
                    </button>
                    {actionsFor(trip)}
                  </Card>
                ))}
              </div>
            )}
          </section>

          {upcoming.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Coming up
              </h2>
              <Card className="p-4">
                <ul className="divide-y divide-line">
                  {upcoming.map((trip) => (
                    <li key={trip.id} className="flex flex-wrap items-center gap-3 py-2">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-ink">{trip.routeName}</span>
                        <span className="block text-[11px] text-subtle">
                          {formatDate(trip.tripDate)} · {humanise(trip.direction)} ·{' '}
                          {formatTime(trip.plannedStartTime)}
                        </span>
                      </span>
                      <TripStatusBadge status={trip.status} />
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/** One rider, thumb-first — identity on top, the server's mark, three tall buttons. */
function MyRiderRow({
  rider,
  canMark,
  busy,
  onMark,
}: {
  rider: TripRider;
  canMark: boolean;
  busy: boolean;
  onMark: (status: TransportAttendanceStatus) => void;
}) {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">
            {rider.studentName}
            {rider.guest && (
              <span className="ml-2 align-middle">
                <Badge tone="warning">guest</Badge>
              </span>
            )}
          </p>
          <p className="truncate text-[11px] text-subtle">
            {rider.stopName ?? 'no stop'}
            {rider.guardianPhone !== undefined && (
              <>
                {' · '}
                <a className="text-accent" href={`tel:${rider.guardianPhone}`}>
                  {rider.guardianPhone}
                </a>
              </>
            )}
          </p>
          {rider.specialInstructions !== undefined && (
            <p className="mt-0.5 text-[11px] text-[color:var(--sms-warning)]">
              {rider.specialInstructions}
            </p>
          )}
        </div>
        <AttendanceStatusBadge status={rider.status} />
      </div>

      {canMark && (
        <div
          className="mt-2 grid grid-cols-3 gap-1.5"
          role="group"
          aria-label={`Mark ${rider.studentName}`}
        >
          {MARKS.map((choice) => (
            <button
              key={choice.value}
              type="button"
              aria-pressed={rider.status === choice.value}
              disabled={busy}
              onClick={() => onMark(choice.value)}
              className={`min-h-11 rounded-md border px-2 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
                rider.status === choice.value
                  ? choice.tone
                  : 'border-line text-subtle hover:border-line-strong hover:text-muted'
              }`}
            >
              {choice.label}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
